import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import * as Sentry from '@sentry/browser';
import styled from 'styled-components';

import { Flex, Icons, LoadingDots, Text, Theme, Tooltip } from 'baukasten';

import messages from './messages';
import { startRecording, type VoiceRecorder } from '../utils/voiceRecorder';
import { IconButton } from './IconButton';

const FAILURE_TIMEOUT_MS = 4000;

const RecordingDot = styled(Flex)`
  height: 8px;
  width: 8px;
  border-radius: 50%;
  background-color: ${Theme.getColor('red')};
`;

type Failure = 'noSpeech' | 'unavailable';

const RecordingTimer = () => {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    // background tabs throttle intervals while the mic keeps recording, so read the clock
    const startedAt = Date.now();
    const timer = setInterval(
      () => setSeconds(Math.floor((Date.now() - startedAt) / 1000)),
      1000
    );
    return () => clearInterval(timer);
  }, []);

  return (
    <Text.SmallDescription color="grey-dark">
      {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}
    </Text.SmallDescription>
  );
};

type Props = {
  disabled: boolean;
  onTranscript: (text: string) => void;
};

export const VoiceButton = ({ disabled, onTranscript }: Props) => {
  const { formatMessage } = useIntl();
  const [recorder, setRecorder] = useState<VoiceRecorder | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const activeRecorderRef = useRef<VoiceRecorder | null>(null);
  activeRecorderRef.current = recorder;

  useEffect(() => () => activeRecorderRef.current?.discard(), []);

  const showFailure = (kind: Failure) => {
    setFailure(kind);
    setTimeout(() => setFailure(null), FAILURE_TIMEOUT_MS);
  };

  const accept = async (activeRecorder: VoiceRecorder) => {
    setRecorder(null);
    setIsTranscribing(true);
    const text = await activeRecorder.stop();
    setIsTranscribing(false);
    if (text) onTranscript(text);
    else showFailure('noSpeech');
  };

  if (isTranscribing) return <LoadingDots />;

  if (recorder) {
    return (
      <Flex gap={1} alignItems="center">
        <RecordingDot />
        <RecordingTimer />
        <IconButton
          type="button"
          aria-label={formatMessage(messages.voiceDiscard)}
          onClick={() => {
            recorder.discard();
            setRecorder(null);
          }}
        >
          <Icons.Close color="grey-dark" size="14px" noPadding />
        </IconButton>
        <IconButton
          type="button"
          aria-label={formatMessage(messages.voiceAccept)}
          onClick={() => accept(recorder)}
        >
          <Icons.Check color="green-dark" size="14px" noPadding />
        </IconButton>
      </Flex>
    );
  }

  return (
    <Flex gap={1} alignItems="center">
      <Tooltip tooltipContent={formatMessage(messages.voiceRecord)}>
        <IconButton
          type="button"
          // a second click while the microphone is being acquired would open a second stream
          disabled={disabled || isStarting}
          aria-label={formatMessage(messages.voiceRecord)}
          onClick={() => {
            setIsStarting(true);
            startRecording()
              .then(setRecorder, error => {
                // client-side media/setup failure (mic-denial excluded) — FE-only
                if (
                  !(
                    error instanceof DOMException &&
                    error.name === 'NotAllowedError'
                  )
                ) {
                  Sentry.captureException(error);
                }
                showFailure('unavailable');
              })
              .finally(() => setIsStarting(false));
          }}
        >
          <Icons.Mic color="grey-dark" size="20px" />
        </IconButton>
      </Tooltip>
      {failure && (
        <Text.SmallDescription color="red-dark">
          {formatMessage(
            failure === 'noSpeech'
              ? messages.voiceNoSpeech
              : messages.voiceUnavailable
          )}
        </Text.SmallDescription>
      )}
    </Flex>
  );
};
